import { useRef, useEffect } from 'react'
import { AuthCard } from '../components'
import { useSignup } from '../hooks'
import { authApi } from '../api/authApi'
import type { SignupCredentials, LoginCredentials } from '../types'
import { toast } from 'react-hot-toast'

const SignUpPage = () => {
  const signupMutation = useSignup()

  const handleSubmit = (credentials: SignupCredentials | LoginCredentials) => {
    signupMutation.mutate(credentials as SignupCredentials)
  }

  // Show toast on error
  useEffect(() => {
    if (signupMutation.error?.message) {
      toast.error(signupMutation.error.message)
    }
  }, [signupMutation.error])

  const gisInitializedRef = useRef(false)

  const handleGoogleAuth = async () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    console.groupCollapsed('[GIS][SignUp] handleGoogleAuth')
    console.log('origin:', window.location.origin)
    console.log('clientId present:', !!clientId)
    console.log('script present:', typeof window.google !== 'undefined')
    console.log('initializedRef:', gisInitializedRef.current)
    
    if (!clientId) {
      console.error('[GIS][SignUp] Missing VITE_GOOGLE_CLIENT_ID')
      console.groupEnd()
      return
    }
    
    const googleId = window.google?.accounts.id
    if (!googleId) {
      console.error('[GIS][SignUp] window.google.accounts.id not available')
      console.groupEnd()
      return
    }

    try { 
      console.log('[GIS][SignUp] cancelling any in-flight session')
      googleId.cancel() 
    } catch (e) { 
      console.warn('[GIS][SignUp] cancel threw (ok to ignore):', e)
    }

    if (!gisInitializedRef.current) {
      console.log('[GIS][SignUp] initializing GIS...')
      googleId.initialize({
        client_id: clientId,
        callback: async (response: { credential?: string }) => {
          try {
            console.log('[GIS][SignUp] callback fired. Has credential:', !!response?.credential)
            const idToken = response.credential
            if (!idToken) {
              console.error('[GIS][SignUp] No credential in response')
              return
            }
            console.time('[GIS][SignUp] authApi.googleAuth')
            const user = await authApi.googleAuth(idToken)
            console.timeEnd('[GIS][SignUp] authApi.googleAuth')
            const next = user.role === 'PENDING' ? '/onboarding/role' : '/'
            console.log('[GIS][SignUp] Auth successful, redirecting to', next)
            window.location.href = next
          } catch (e) {
            console.error('[GIS][SignUp] Google signup failed', e)
            // Show user-friendly error
            toast.error('Google authentication failed. Please try again or use email signup.')
          }
        },
        auto_select: false,
        // Remove use_fedcm_for_prompt to avoid FedCM issues for now
        cancel_on_tap_outside: false,
      })
      gisInitializedRef.current = true
      console.log('[GIS][SignUp] initialized set -> true')
    }
    
    // In dev, force OAuth code popup flow first
    if (import.meta.env.DEV) {
      try {
        const codeClient = window.google?.accounts.oauth2.initCodeClient({
          client_id: clientId,
          scope: 'email profile openid',
          ux_mode: 'popup',
          redirect_uri: 'postmessage',
          callback: async (resp: { code?: string; error?: string }) => {
            if (resp.error) {
              console.error('[GIS][SignUp][DEV] Code client error:', resp.error)
              toast.error(`Google Sign-In Error: ${resp.error}`)
              return
            }
            if (resp.code) {
              try {
                await authApi.googleAuthWithCode(resp.code)
                window.location.href = '/'
              } catch (err) {
                console.error('[GIS][SignUp][DEV] Backend code exchange failed:', err)
                if (err instanceof Error) {
                  toast.error(err.message)
                } else {
                  toast.error('Google Sign-In Failed')
                }
              }
            }
          }
        })
        codeClient?.requestCode()
        console.groupEnd()
        return
      } catch (err) {
        console.warn('[GIS][SignUp][DEV] Code flow init failed, falling back to prompt:', err)
      }
    }

    try {
      console.log('[GIS][SignUp] prompting...')
      googleId.prompt((notification: { getDismissedReason?: () => string; getNotDisplayedReason?: () => string }) => {
        try {
          const reason = notification?.getDismissedReason?.() || notification?.getNotDisplayedReason?.()
          if (reason) {
            console.warn('[GIS][SignUp] prompt dismissed/not displayed:', reason)
            if (reason === 'credential_returned' || reason === 'cancel_called') {
              // These are normal, don't show error
              return
            }
          }
        } catch (err) {
          console.warn('[GIS][SignUp] prompt reason retrieval failed:', err)
        }
      })
    } catch (e) {
      console.error('[GIS][SignUp] Google prompt error, falling back to OAuth code flow', e)
      try {
        const client = window.google?.accounts.oauth2.initCodeClient({
          client_id: clientId,
          scope: 'email profile openid',
          ux_mode: 'popup',
          redirect_uri: 'postmessage',
          callback: async (resp: { code?: string; error?: string }) => {
            if (resp.error) {
              console.error('[GIS][SignUp] Code client error:', resp.error)
              return
            }
            if (resp.code) {
              try {
                const user = await authApi.googleAuthWithCode(resp.code)
                const next = user.role === 'PENDING' ? '/onboarding/role' : '/'
                window.location.href = next
              } catch (err) {
                console.error('[GIS][SignUp] Backend code exchange failed:', err)
                if (err instanceof Error) {
                  toast.error(err.message)
                } else {
                  toast.error('Google Sign-In Failed')
                }
              }
            }
          }
        })
        client?.requestCode()
      } catch (err) {
        console.error('[GIS][SignUp] Code flow init failed:', err)
        toast.error('Failed to show Google sign-in. Please try refreshing the page.')
      }
    }
    console.groupEnd()
  }

  return (
    <AuthCard
      type="signup"
      title="Create your account"
      subtitle="Join us to manage your sober living facilities"
      onSubmit={handleSubmit}
      onGoogleAuth={handleGoogleAuth}
      isLoading={signupMutation.isPending}
      error={signupMutation.error?.message}
    />
  )
}

export default SignUpPage